/**
 * Read an image's natural dimensions, an average color, and a rough "interest" score, without
 * keeping the decode around.
 *
 * All three come from one downsample. Dimensions let masonry lay out without shift, the color is
 * the LQIP placeholder (PLAN.md §6.5 — a hard requirement, not polish), and interest breaks ties
 * when several photos fit the same editorial slot (§6.2).
 */

export interface ImageProbe {
  width: number;
  height: number;
  /** CSS `rgb(...)` average of the image. */
  placeholder: string;
  /** 0–1. Colourfulness and contrast combined — a crude proxy, used only to break ties. */
  interest: number;
}

const FALLBACK: ImageProbe = {
  width: 4,
  height: 3,
  placeholder: 'rgb(30,41,59)',
  interest: 0.5,
};

/** 8×8 = 64 samples: enough spread for a meaningful variance, still trivial to compute. */
const SWATCH = 8;

interface SwatchStats {
  placeholder: string;
  interest: number;
}

/**
 * Colourfulness follows Hasler & Süsstrunk's opponent-channel metric; contrast is the standard
 * deviation of luminance. Both are normalised generously — this only needs to rank photos
 * against each other, not measure anything absolutely.
 */
function analyse(source: CanvasImageSource, w: number, h: number): SwatchStats {
  const canvas = document.createElement('canvas');
  canvas.width = SWATCH;
  canvas.height = SWATCH;
  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) return { placeholder: FALLBACK.placeholder, interest: FALLBACK.interest };

  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'low';
  ctx.drawImage(source, 0, 0, w, h, 0, 0, SWATCH, SWATCH);

  let data: Uint8ClampedArray;
  try {
    data = ctx.getImageData(0, 0, SWATCH, SWATCH).data;
  } catch {
    // Tainted canvas — can't happen for blobs we fetched ourselves, but be safe.
    return { placeholder: FALLBACK.placeholder, interest: FALLBACK.interest };
  }

  const pixels = SWATCH * SWATCH;
  let sumR = 0;
  let sumG = 0;
  let sumB = 0;
  const rg: number[] = [];
  const yb: number[] = [];
  const luma: number[] = [];

  for (let i = 0; i < data.length; i += 4) {
    const r = data[i];
    const g = data[i + 1];
    const b = data[i + 2];
    sumR += r;
    sumG += g;
    sumB += b;
    rg.push(r - g);
    yb.push(0.5 * (r + g) - b);
    luma.push(0.2126 * r + 0.7152 * g + 0.0722 * b);
  }

  const mean = (xs: number[]) => xs.reduce((a, x) => a + x, 0) / xs.length;
  const stdDev = (xs: number[]) => {
    const m = mean(xs);
    return Math.sqrt(xs.reduce((a, x) => a + (x - m) ** 2, 0) / xs.length);
  };

  const meanRg = mean(rg);
  const meanYb = mean(yb);
  const colourfulness =
    Math.sqrt(stdDev(rg) ** 2 + stdDev(yb) ** 2) +
    0.3 * Math.sqrt(meanRg ** 2 + meanYb ** 2);

  // ~110 and ~80 sit near the top of what real photos produce; clamping keeps outliers honest.
  const colourScore = Math.min(colourfulness / 110, 1);
  const contrastScore = Math.min(stdDev(luma) / 80, 1);

  return {
    placeholder: `rgb(${Math.round(sumR / pixels)},${Math.round(sumG / pixels)},${Math.round(sumB / pixels)})`,
    interest: Math.round((colourScore * 0.55 + contrastScore * 0.45) * 1000) / 1000,
  };
}

async function probeViaImageBitmap(blob: Blob): Promise<ImageProbe> {
  const bitmap = await createImageBitmap(blob);
  try {
    return {
      width: bitmap.width,
      height: bitmap.height,
      ...analyse(bitmap, bitmap.width, bitmap.height),
    };
  } finally {
    // Full-size decode — release it immediately or probing a folder blows out memory.
    bitmap.close();
  }
}

function probeViaImageElement(blob: Blob): Promise<ImageProbe> {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(blob);
    const img = new Image();
    img.onload = () => {
      const probe: ImageProbe = {
        width: img.naturalWidth,
        height: img.naturalHeight,
        ...analyse(img, img.naturalWidth, img.naturalHeight),
      };
      URL.revokeObjectURL(url);
      resolve(probe);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(FALLBACK);
    };
    img.src = url;
  });
}

export async function probeImage(blob: Blob): Promise<ImageProbe> {
  try {
    if (typeof createImageBitmap === 'function') return await probeViaImageBitmap(blob);
  } catch {
    // Some engines reject createImageBitmap for certain formats (older Safari + HEIC).
    // Fall through to the <img> path, which also fails gracefully.
  }
  return probeViaImageElement(blob);
}

export const FALLBACK_PLACEHOLDER = FALLBACK.placeholder;
export const FALLBACK_INTEREST = FALLBACK.interest;

/**
 * Run `task` over `items` with at most `limit` in flight, reporting completions.
 * Probing decodes full-size images, so unbounded parallelism is how you OOM a phone.
 */
export async function mapWithConcurrency<T, R>(
  items: readonly T[],
  limit: number,
  task: (item: T, index: number) => Promise<R>,
  onSettled?: (completed: number) => void,
  signal?: AbortSignal
): Promise<R[]> {
  const results = new Array<R>(items.length);
  let cursor = 0;
  let completed = 0;

  const workers = Array.from({ length: Math.min(limit, items.length) }, async () => {
    for (;;) {
      const index = cursor++;
      if (index >= items.length) return;
      if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
      results[index] = await task(items[index], index);
      onSettled?.(++completed);
    }
  });

  await Promise.all(workers);
  return results;
}
